import { useOrganizationSidebarItems } from '_/components/Organizations/OrganizationSidebar';
import { useSidebar, CONTEXT_DEPTH } from '_/contexts/LayoutContext';
import { OrganizationProvider, useOrganization } from '_/contexts/OrganizationContext';
import React, { ReactNode, useEffect } from 'react';

interface OrganizationSectionProps {
  children: ReactNode;
}

/**
 * Sidebar manager that consumes OrganizationContext
 * Separated from provider to avoid circular dependency
 */
function OrganizationSidebarManager({ children }: OrganizationSectionProps) {
  const { selectedOrgSlug } = useOrganization();
  const { setSidebarItems, registerContext, unregisterContext } = useSidebar();
  const sidebarItems = useOrganizationSidebarItems(selectedOrgSlug || '');

  useEffect(() => {
    registerContext(CONTEXT_DEPTH.ORGANIZATION);
    return () => {
      unregisterContext(CONTEXT_DEPTH.ORGANIZATION);
    };
  }, [registerContext, unregisterContext]);

  useEffect(() => {
    if (selectedOrgSlug && selectedOrgSlug !== '_new' && sidebarItems.length > 0) {
      setSidebarItems(sidebarItems, CONTEXT_DEPTH.ORGANIZATION);
    }
  }, [setSidebarItems, sidebarItems, selectedOrgSlug]);

  return <>{children}</>;
}

/**
 * OrganizationSection - Wrapper for organization-level pages
 * Combines provider and sidebar manager
 */
function OrganizationSection({ children }: OrganizationSectionProps) {
  return (
    <OrganizationProvider>
      <OrganizationSidebarManager>{children}</OrganizationSidebarManager>
    </OrganizationProvider>
  );
}

export default OrganizationSection;
